import { ApolloError } from "@apollo/client";
import { Locale } from "date-fns";
import {
  EnumEventAccessOperatorStatus,
  EnumOwnerType,
  EventByIdQuery,
} from "gql/generated";
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
} from "react";
import { MultilingualLangKey } from "shared/types";
import { LocationListData } from "apps/events/types/LocationListData";
import {
  ComboboxData,
  ComboboxGroupData,
} from "apps/events/types/ComboboxData";
import { ModalMode } from "apps/events/types/ModalMode";

export type EventCreateEditContextValues = {
  isOpen: boolean;
  mode: ModalMode;
  userEmailAddress: string;
  userFirstName: string;
  userLastName: string;
  isOwner: boolean;
  /** True when the current user is an organization admin (Owner role) for the current org. */
  isOrganizationAdmin: boolean;
  /** True when the current user is a TNB internal user (can delete draft events). */
  isTnbInternalUser: boolean;
  userType: EnumOwnerType | "PUBLIC";
  isOrganizationUser: boolean;
  isOperatorUser: boolean;
  defaultOrganizationId: string;
  defaultOperatorEntityIds: string[];
  /** Storefront connected listings offered as the event organizer. */
  organizerEntityIds?: string[];
  defaultOrganizationIdsForOperatorUser: string[];
  /** Organization tag ids pre-applied to new events (e.g. from the submit widget's `tag-ids`). */
  defaultOrganizationTagIds?: string[];
  limitHostToOperatorEntityId?: string;
  lang: MultilingualLangKey;
  locale: Locale | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  // EVENT
  eventId?: string;
  event: EventByIdQuery["eventById"];
  isFetchingEvent: boolean;
  fetchEventError: ApolloError | undefined;
  status: EnumEventAccessOperatorStatus;
  isSavingStatusChange: boolean;
  isConfirmationModalOpen: boolean;
  confirmationModalMessage: string;
  handleApproveClick: () => void;
  handleAcceptAttentionIssuesClick: () => void;
  handleConfirmationModalActionClick: (confirmed: boolean) => void;
  handleEditClick: () => void;
  isMultiOrgEvent: boolean;
  handleMenuAction: ({
    value,
  }: {
    value: "cancel" | "uncancel" | "decline" | "delete" | "disconnect";
  }) => void;
  updateEvent: (event: EventByIdQuery["eventById"]) => void;
  // MODAL
  onClose: () => void;
  hideMenuButtons?: boolean;
  // EVENT STEP TRACKING
  currentStep: number;
  totalSteps: number;
  handleNextClick: () => void;
  handlePreviousClick: () => void;
  handleJumpToStepClick: (step: number) => void;
  isVirtual: boolean;
  setIsVirtual: Dispatch<SetStateAction<boolean>>;
  // STEP 1
  globalEventTags: ComboboxGroupData[];
  organizationEventTags: ComboboxData[];
  // STEP 2
  allLocations: LocationListData[];
  searchResults: LocationListData[];
  searchOperators: (search: string) => void;
  prefetchOperatorsByEntityIds: (entityIds: string[]) => void;
  isSearchingOperators: boolean;
  /** Operator users preload their own listings for the organizer field. */
  useOperatorPreload: boolean;
  // PERSON DETAILS (submitter card)
  onOpenPersonDetails?: (personId: string) => void;
  personDetailsPersonId: string | null;
  isPersonDetailsOpen: boolean;
  onClosePersonDetails: () => void;
  setPersonDetailsPersonId: (id: string | null) => void;
};

export type EventCreateEditContextProps = {
  initialMode: ModalMode;
  initialStep?: number;
  onEventUpdated: (event: EventByIdQuery["eventById"]) => void;
  onEventDeleted: (eventId: string) => void;
  hideMenuButtons?: boolean;
  isOrganizationAdmin?: boolean;
  isTnbInternalUser?: boolean;
  /** Pre-built organizer location from the parent page, bypasses operatorMany query. */
  preloadedOrganizerLocation?: LocationListData;
} & Omit<
  EventCreateEditContextValues,
  | "mode"
  | "isOwner"
  | "isMultiOrgEvent"
  | "isOrganizationUser"
  | "isOperatorUser"
  | "lang"
  | "locale"
  | "event"
  | "isFetchingEvent"
  | "fetchEventError"
  | "status"
  | "isSavingStatusChange"
  | "isConfirmationModalOpen"
  | "confirmationModalMessage"
  | "handleApproveClick"
  | "handleAcceptAttentionIssuesClick"
  | "handleConfirmationModalActionClick"
  | "handleEditClick"
  | "handleMenuAction"
  | "updateEvent"
  | "loading"
  | "error"
  | "currentStep"
  | "totalSteps"
  | "handleNextClick"
  | "handlePreviousClick"
  | "handleJumpToStepClick"
  | "isVirtual"
  | "setIsVirtual"
  | "globalEventTags"
  | "organizationEventTags"
  | "allLocations"
  | "searchResults"
  | "searchOperators"
  | "prefetchOperatorsByEntityIds"
  | "isSearchingOperators"
  | "useOperatorPreload"
  | "onOpenPersonDetails"
  | "personDetailsPersonId"
  | "isPersonDetailsOpen"
  | "onClosePersonDetails"
  | "setPersonDetailsPersonId"
> &
  PropsWithChildren & {
    onOpenPersonDetails?: (personId: string) => void;
    personDetailsPersonId?: string | null;
    isPersonDetailsOpen?: boolean;
    onClosePersonDetails?: () => void;
    setPersonDetailsPersonId?: (id: string | null) => void;
  };

export const EventCreateEditContext =
  createContext<EventCreateEditContextValues | null>(null);
